import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  Download,
  Languages,
  ListChecks,
  Menu,
  PanelLeft,
  Plug,
  Settings2,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { WorkspaceRecoveryPanel } from "@/features/workspaces";
import { WorkspaceProvider, useWorkspace } from "@/domain/provider";
import { conflictsForShift } from "@/domain/selectors";
import { Drawer, ErrorMessage, type RouteProps } from "@/features/shared";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Badge } from "@/components/ui/badge";
import { tx, getLocale, setLocale, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { UserGuide, GuideCoach } from '@/features/guide';
import { RouteErrorBoundary } from '@/components/route-error-boundary';
import { RouteLoadRecovery } from '@/features/route-load-recovery';

const UnitsPage = lazy(() =>
  import("@/features/units").then((module) => ({ default: module.UnitsPage })),
);
const RulesPage = lazy(() =>
  import("@/features/rules").then((module) => ({ default: module.RulesPage })),
);
const WorkforcePage = lazy(() =>
  import("@/features/workforce").then((module) => ({
    default: module.WorkforcePage,
  })),
);
const PeoplePage = lazy(() =>
  import("@/features/workforce").then((module) => ({
    default: module.PeoplePage,
  })),
);
const IntegrationsPage = lazy(() =>
  import("@/features/integrations").then((module) => ({
    default: module.IntegrationsPage,
  })),
);
const WorkloadPage = lazy(() =>
  import("@/features/workload").then((module) => ({
    default: module.WorkloadPage,
  })),
);
const BookingsPage = lazy(() =>
  import("@/features/bookings").then((module) => ({
    default: module.BookingsPage,
  })),
);
const ShiftsPage = lazy(() =>
  import("@/features/shifts").then((module) => ({
    default: module.ShiftsPage,
  })),
);
const ChangesPage = lazy(() =>
  import("@/features/changes").then((module) => ({
    default: module.ChangesPage,
  })),
);
const TodayPage = lazy(() =>
  import("@/features/changes").then((module) => ({
    default: module.TodayPage,
  })),
);

const pages = [
  { id: "today", label: "Today", icon: Activity, group: "Operations" },
  { id: "shifts", label: "Planning", icon: CalendarDays, group: "Operations" },
  {
    id: "workload",
    label: "Assignments",
    icon: ClipboardList,
    group: "Workload",
  },
  { id: "bookings", label: "Bookings", icon: CalendarDays, group: "Workload" },
  { id: "units", label: "Units", icon: Building2, group: "Workload" },
  {
    id: "workforce",
    label: "Calendar",
    icon: CalendarDays,
    group: "Workforce",
  },
  { id: "people", label: "People", icon: Users, group: "Workforce" },
  { id: "changes", label: "Needs attention", icon: Bell, group: "Operations" },
  {
    id: "rules",
    label: "Properties & work rules",
    icon: ListChecks,
    group: "Administration",
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: Plug,
    group: "Administration",
  },
];
export default function App() {
  return (
    <WorkspaceProvider>
      <WorkspaceShell />
    </WorkspaceProvider>
  );
}
function WorkspaceShell() {
  const {state}=useWorkspace();
  return <OperationsApp key={state.id??'opening-workspace'}/>;
}
function OperationsApp() {
  const { state, ready, error, clearError, reload, practiceRun, readCurrent } = useWorkspace();
  const [routeFailurePage,setRouteFailurePage] = useState<string|null>(null);
  const guideOpener=useRef<HTMLElement|null>(null)
  const openUserGuide=()=>{guideOpener.current=document.activeElement instanceof HTMLElement?document.activeElement:null;setGuideOpen(true)}
  const closeUserGuide=()=>{setGuideOpen(false);requestAnimationFrame(()=>{const element=guideOpener.current;if(element?.isConnected&&element.getClientRects().length&&!element.closest('[inert], [aria-hidden="true"]'))element.focus({preventScroll:true})})}
  const [query, setQuery] = useState(
    () => new URLSearchParams(location.search),
  );
  const routeRef = useRef(query);
  routeRef.current = query;
  const [mobileNav, setMobileNav] = useState(false);
  const [desktopNav, setDesktopNav] = useState(true);
  const [compactNav,setCompactNav] = useState(()=>window.matchMedia('(max-width: 1023px)').matches);
  const sidebarRef=useRef<HTMLElement>(null);
  const navToggleRef=useRef<HTMLButtonElement>(null);
  useEffect(()=>{
    const media=window.matchMedia('(max-width: 1023px)');
    const update=()=>setCompactNav(media.matches);media.addEventListener('change',update);
    return ()=>media.removeEventListener('change',update);
  },[]);
  useEffect(()=>{
    if(!mobileNav || !compactNav) return;
    sidebarRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const escape=(event:KeyboardEvent)=>{if(event.key==='Escape'){setMobileNav(false);navToggleRef.current?.focus();}};
    window.addEventListener('keydown',escape);return()=>window.removeEventListener('keydown',escape);
  },[mobileNav,compactNav]);
  const [locale, updateLocale] = useState<Locale>(getLocale);
  const [debug, setDebug] = useState(false);
  const [guideOpen,setGuideOpen]=useState(false);
  useEffect(()=>{if(query.get('tools')==='recovery')setDebug(true)},[query.get('tools')]);
  const [search, setSearch] = useState("");
  const savedRoutes = useRef(new Map<string, string>());
  const routePositions = useRef(new Map<string, {x:number;y:number;focus?:string}>());
  const mainRef = useRef<HTMLElement>(null);
  const currentPage = pages.some((item) => item.id === query.get("page"))
    ? query.get("page")!
    : "today";
  const page = pages.find((item) => item.id === currentPage)!;
  const clockPropertyId = query.get('property') || state.shifts.find(item => item.id === query.get('id'))?.propertyId;
  const demoTimeZone = state.properties.find(item => item.id === clockPropertyId)?.timeZone ?? state.properties[0]?.timeZone ?? 'Europe/Helsinki';
  const previousWorkspace = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!ready || !state.id || previousWorkspace.current === state.id) return;
    const switchingWorkspace = previousWorkspace.current !== undefined;
    previousWorkspace.current = state.id;
    const next = new URLSearchParams(location.search);
    if (next.has('id') && next.get('id') !== 'new' && !state.shifts.some(item => item.id === next.get('id'))) next.delete('id');
    if (next.has('property') && !state.properties.some(item => item.id === next.get('property'))) next.delete('property');
    if (switchingWorkspace && state.fixtureVersion && currentPage === 'shifts') { next.delete('date'); next.delete('allDates'); next.delete('assignment'); next.delete('task'); }
    const target = new URL(location.href); target.search = next.toString();
    history.replaceState(null, '', target); setQuery(next);
  }, [ready, state.id]);
  useEffect(() => {
    const pop = () => {
      const next = new URLSearchParams(location.search);
      const current = routeRef.current;
      const leavingShift =
        current.get("page") === "shifts" &&
        ['page','id','property','date','session','newPlan'].some(key => next.get(key) !== current.get(key));
      const proceed = () => {
        routePositions.current.set(current.toString(), {x:window.scrollX,y:window.scrollY,focus:document.activeElement?.id || undefined});
        routeRef.current=next;
        setQuery(next); setMobileNav(false);
        const saved = routePositions.current.get(next.toString());
        requestAnimationFrame(() => { window.scrollTo(saved?.x ?? 0, saved?.y ?? 0); if(saved?.focus) document.getElementById(saved.focus)?.focus({preventScroll:true}); });
      };
      if (
        !leavingShift ||
        window.dispatchEvent(
          new CustomEvent("luuta:before-navigate", {
            cancelable: true,
            detail: { proceed },
          }),
        )
      )
        proceed();
    };
    addEventListener("popstate", pop);
    return () => removeEventListener("popstate", pop);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const onQuery: RouteProps["onQuery"] = (patch, options = {}) => {
    const perform = () => {
      const previous=routeRef.current,fromPage=previous.get('page')??currentPage;
      const goingElsewhere = patch.page && patch.page !== fromPage;
      routePositions.current.set(routeRef.current.toString(), {x:window.scrollX,y:window.scrollY,focus:document.activeElement?.id || undefined});
      if (goingElsewhere)
        savedRoutes.current.set(fromPage, previous.toString());
      const next = new URLSearchParams(
        goingElsewhere
          ? savedRoutes.current.get(patch.page!) || ""
          : routeRef.current,
      );
      if (goingElsewhere) next.set("page", patch.page!);
      for (const [key, value] of Object.entries(patch))
        value === null || value === ""
          ? next.delete(key)
          : next.set(key, value);
      if (!next.has("page")) next.set("page", fromPage);
      if(practiceRun)next.set('practice',practiceRun.id);
      const target = new URL(location.href);
      target.search = next.toString();
      if (options.replace) history.replaceState(null, "", target);
      else history.pushState(null, "", target);
      const fullPage = goingElsewhere || (patch.id !== undefined && patch.id !== previous.get('id'));
      routeRef.current=next;
      setQuery(next);
      setMobileNav(false);
      setSearch("");
      if (fullPage) requestAnimationFrame(() => { window.scrollTo(0,0); mainRef.current?.focus({preventScroll:true}); });
    };
    const leavingShift =
      currentPage === "shifts" &&
      ['page','id','property','date','session','newPlan'].some(key =>
        patch[key] !== undefined && (patch[key] || null) !== query.get(key));
    if (
      options.skipGuard ||
      !leavingShift ||
      window.dispatchEvent(
        new CustomEvent("luuta:before-navigate", {
          cancelable: true,
          detail: { proceed: perform },
        }),
      )
    )
      perform();
  };
  const pending = state.impacts.filter((item) => item.status === "pending");
  const attentionCount = new Set([
    ...pending.map(item => `${item.shiftId ?? item.propertyId}:${item.subjectId}`),
    ...state.shifts.filter(item => ['Planned','Active'].includes(item.status)).flatMap(shift => conflictsForShift(state, shift.id, state.demoClockAt).map(item => `${shift.id}:${item.personId ?? item.assignmentId ?? item.code}`)),
  ]).size;
  const active = state.shifts.filter((item) => item.status === "Active");
  const exportWorkspace = () => {
    const blob = new Blob([JSON.stringify(readCurrent(), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `luuta-workspace-v2-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const content = !ready ? (
    <div data-testid="workspace-loading" className="mx-auto max-w-xl py-24">
      <p className="mb-4 text-lg font-semibold">
        {tx("ops.opening.your.operations.workspace.1jk5jv5")}
      </p>
      <ErrorMessage error={error} />
      {error && (
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => void reload()}
        >
          {tx("ops.retry.zkouah")}
        </Button>
      )}
      <p className="mt-4 text-sm leading-6 text-muted-foreground">
        {tx("ops.existing.data.is.preserved.legacy.records.1kg60dx")}
      </p>
    </div>
  ) : currentPage === "units" ? (
    <UnitsPage query={query} onQuery={onQuery} />
  ) : currentPage === "rules" ? (
    <RulesPage query={query} onQuery={onQuery} />
  ) : currentPage === "bookings" ? (
    <BookingsPage query={query} onQuery={onQuery} />
  ) : currentPage === "workload" ? (
    <WorkloadPage query={query} onQuery={onQuery} />
  ) : currentPage === "workforce" ? (
    <WorkforcePage query={query} onQuery={onQuery} />
  ) : currentPage === "people" ? (
    <PeoplePage query={query} onQuery={onQuery} />
  ) : currentPage === "integrations" ? (
    <IntegrationsPage query={query} onQuery={onQuery} />
  ) : currentPage === "shifts" ? (
    <ShiftsPage query={query} onQuery={onQuery} />
  ) : currentPage === "changes" ? (
    <ChangesPage query={query} onQuery={onQuery} />
  ) : (
    <TodayPage query={query} onQuery={onQuery} />
  );
  return (
    <div className={cn("min-h-screen bg-background text-foreground",practiceRun&&"guide-practice-shell")}>
      {mobileNav && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-foreground/30 lg:hidden"
          aria-label={tx("ops.close.navigation.1ynuomf")}
          onClick={() => {setMobileNav(false);requestAnimationFrame(()=>navToggleRef.current?.focus());}}
        />
      )}
      <aside
        id="main-sidebar"
        ref={sidebarRef}
        inert={compactNav ? !mobileNav : !desktopNav}
        aria-hidden={compactNav ? !mobileNav : !desktopNav}
        className={cn(
          "fixed inset-y-0 start-0 z-40 flex w-[216px] flex-col border-e border-border bg-muted/60 transition-transform",
          mobileNav ? "translate-x-0" : "-translate-x-full",
          desktopNav ? "lg:translate-x-0" : "lg:-translate-x-full",
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-5">
          <span className="grid size-8 place-items-center rounded-lg bg-primary text-primary-foreground">
            <Building2 className="size-4" />
          </span>
          <div>
            <p className="text-base font-semibold tracking-tight">Luuta</p>
            <p className="sr-only">
              {tx("ops.operations.workspace.aeoh3y")}
            </p>
          </div>
        </div>
        <nav
          aria-label={tx("ops.main.navigation.1iucaq2")}
          className="flex-1 overflow-y-auto p-3"
        >
          {["Operations", "Workload", "Workforce", "Administration"].map(
            (group) => (
              <section key={group} className="mb-5">
                <h2 className="mb-2 break-words px-3 text-sm font-semibold text-muted-foreground">
                  {tx(group)}
                </h2>
                {pages
                  .filter((item) => item.group === group)
                  .map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onQuery({ page: item.id })}
                      aria-current={
                        currentPage === item.id ? "page" : undefined
                      }
                      className={cn(
                        "mb-1 flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-start text-sm transition-colors hover:bg-secondary",
                        currentPage === item.id
                          ? "bg-secondary font-semibold text-foreground"
                          : "text-muted-foreground",
                      )}
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="flex-1">{tx(item.label)}</span>
                      {item.id === "changes" && attentionCount > 0 && (
                        <Badge variant="warning" className="px-1.5">
                          {attentionCount}
                        </Badge>
                      )}
                    </button>
                  ))}
              </section>
            ),
          )}
          {active.length > 0 && (
            <section className="rounded-lg border border-border bg-card p-3">
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-success-foreground">
                <span className="size-1.5 rounded-full bg-success" />
                {tx("ops.active.shifts.3alxqs")}
              </p>
              {active.map((shift) => (
                <button
                  key={shift.id}
                  className="flex w-full items-center justify-between gap-2 py-2 text-start text-xs font-medium leading-5 text-success-foreground hover:underline"
                  onClick={() =>
                    onQuery({ page: "shifts", id: shift.id, tab: "schedule" })
                  }
                >
                  {shift.name}
                  <ChevronRight className="size-3 shrink-0" />
                </button>
              ))}
            </section>
          )}
        </nav>
        <div className="border-t border-border p-3">
          <button data-guide="guide.open" className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium hover:bg-secondary" onClick={openUserGuide}><CircleHelp className="size-4"/>{tx('User guide')}</button>
          <button
            className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-sm text-muted-foreground hover:bg-secondary"
            onClick={() => {onQuery({tools:'recovery'});setDebug(true)}}
          >
            <Settings2 className="size-4" />
            {tx("ops.prototype.tools.pth78y")}
          </button>
          <div className="mt-3 flex items-center gap-3 px-3 pb-2">
            <span className="grid size-8 place-items-center rounded-full bg-primary/10 text-xs font-semibold">
              AD
            </span>
            <div>
              <p className="text-xs font-semibold">
                {tx("ops.admin.dispatcher.shvo3e")}
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {tx("ops.local.prototype.1lk61im")}
              </p>
            </div>
          </div>
        </div>
      </aside>
      <div
        inert={compactNav && mobileNav}
        className={cn("min-w-0 transition-[padding]", desktopNav && "lg:ps-[216px]")}
      >
        <header className="sticky top-0 z-20 flex h-12 items-center justify-between gap-3 border-b border-border bg-card/95 px-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              ref={navToggleRef}
              size="icon"
              variant="ghost"
              className="lg:hidden"
              aria-label={tx("ops.open.navigation.1f2882l")}
              aria-expanded={mobileNav}
              aria-controls="main-sidebar"
              onClick={() => setMobileNav(true)}
            >
              <Menu />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="hidden lg:inline-flex"
              aria-label={tx(
                desktopNav
                  ? "ops.collapse.navigation.a3qggg"
                  : "ops.expand.navigation.1tge991",
              )}
              onClick={() => setDesktopNav((value) => !value)}
            >
              <PanelLeft />
            </Button>
            <nav
              aria-label={tx("ops.breadcrumb.dfc3fw")}
              className="hidden min-w-0 truncate text-sm min-[400px]:block"
            >
              <span className="hidden text-muted-foreground sm:inline">
                {tx(page.group)} /{" "}
              </span>
              <span className="font-semibold">{tx(page.label)}</span>
              {query.get("id") && (
                <span className="text-muted-foreground">
                  {" "}
                  /{" "}
                  {tx(
                    query.get("id") === "new"
                      ? "ops.new.shift.1o7ezlt"
                      : "ops.details.43f6md",
                  )}
                </span>
              )}
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {state.demoClockAt && <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-sm tabular-nums" data-testid="demo-clock" data-time-zone={demoTimeZone} title={demoTimeZone}><span className={practiceRun?'':'hidden min-[480px]:inline'}>{tx(practiceRun?'Practice':'Demo')}<span className={practiceRun?'hidden min-[480px]:inline':''}> · </span></span><span className={practiceRun?'hidden min-[480px]:inline':''}>{new Date(state.demoClockAt).toLocaleTimeString(locale, {hour:'2-digit',minute:'2-digit',hourCycle:'h23',timeZone:demoTimeZone})}</span><span className="sr-only"> {demoTimeZone}</span></span>}
            <div className="relative hidden 2xl:block">
              <input
                className="h-9 w-52 rounded-md border border-border bg-muted/30 px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                value={search}
                placeholder={tx("ops.find.a.view.nidyzw")}
                aria-label={tx("ops.find.a.view.1oz1x6q")}
                onChange={(event) => setSearch(event.target.value)}
              />
              {search && (
                <div className="absolute end-0 top-11 w-64 rounded-xl border border-border bg-card p-2 shadow-lg">
                  {pages
                    .filter((item) =>
                      tx(item.label)
                        .toLowerCase()
                        .includes(search.toLowerCase()),
                    )
                    .map((item) => (
                      <Button
                        key={item.id}
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => onQuery({ page: item.id })}
                      >
                        {tx(item.label)}
                      </Button>
                    ))}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label={tx("ops.open.changes.1ssgi7o")}
              onClick={() => onQuery({ page: "changes" })}
              className={cn("relative",practiceRun&&"hidden min-[480px]:inline-flex")}
            >
              <Bell />
              {attentionCount > 0 && (
                <span className="absolute end-1 top-1 size-2 rounded-full bg-warning" />
              )}
            </Button>
            <Button data-guide="guide.open" variant="ghost" size="icon" className="hidden min-[360px]:inline-flex" aria-label={tx('User guide')} onClick={openUserGuide}><CircleHelp/></Button>
            <label className="flex items-center gap-1 rounded-md border border-border px-2">
              <Languages className="size-3.5 text-muted-foreground" />
              <NativeSelect
                className="h-11 w-12 shrink-0 border-0 bg-transparent px-1 shadow-none"
                aria-label={tx("ops.interface.language.sk0cbe")}
                value={locale}
                onChange={(event) => {
                  const value = event.target.value as Locale;
                  setLocale(value);
                  updateLocale(value);
                }}
              >
                {["en", "fi", "uk", "ru"].map((value) => (
                  <option key={value} value={value}>
                    {value.toUpperCase()}
                  </option>
                ))}
              </NativeSelect>
            </label>
          </div>
        </header>
        <main ref={mainRef} tabIndex={-1} className={cn("min-w-0 outline-none", currentPage === "shifts" ? "app-planning" : "px-4 py-5 sm:px-6 lg:px-7")}>
          {ready && error && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
              <p
                role="alert"
                className="flex-1 text-sm leading-6 text-destructive"
              >
                {tx(error)}
              </p>
              <Button variant="ghost" size="sm" onClick={clearError}>
                {tx("ops.dismiss.an1pf7")}
              </Button>
            </div>
          )}
          <RouteErrorBoundary key={currentPage} onError={()=>setRouteFailurePage(currentPage)} fallback={<RouteLoadRecovery onExport={exportWorkspace}/>}>
          <Suspense
            fallback={
              <p data-testid="view-loading" role="status" className="py-12 text-sm text-muted-foreground">
                {tx("ops.opening.your.operations.workspace.1jk5jv5")}
              </p>
            }
          >
          <div key={state.id ?? 'loading'}>{content}</div>
          </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
      {debug && (
        <Drawer
          title="ops.prototype.tools.pth78y"
          description="ops.local.workspace.storage.and.recovery.lh6v8m"
          onClose={() => {setDebug(false);if(query.has('tools'))onQuery({tools:null},{replace:true})}}
          footer={
            <Button variant="outline" onClick={exportWorkspace}>
              <Download />
              {tx("ops.export.workspace.backup.1rm3h54")}
            </Button>
          }
        >
          <WorkspaceRecoveryPanel/>
          <p className="mt-5 text-sm leading-6 text-muted-foreground">
            {tx("ops.this.prototype.uses.one.canonical.indexeddb.wvbpcm")}
          </p>
          <dl className="my-5 grid grid-cols-2 gap-3 text-sm">
            <dt>{tx("ops.schema.16bx8pi")}</dt>
            <dd>2</dd>
            <dt>{tx("ops.revision.1pi2b08")}</dt>
            <dd>{state.revision}</dd>
            <dt>{tx("ops.assignments.1s400d5")}</dt>
            <dd>{state.assignments.length}</dd>
            <dt>{tx("ops.audit.events.d3z1c9")}</dt>
            <dd>{state.audit.length}</dd>
          </dl>
          <p className="mt-4 text-xs leading-5 text-muted-foreground">
            {tx("ops.simulation.runs.and.their.outcomes.are.h20rhu")}
          </p>
        </Drawer>
      )}
      {guideOpen&&<UserGuide onClose={closeUserGuide}/>}
      <GuideCoach query={query} onQuery={onQuery} hidden={guideOpen} routeUnavailable={routeFailurePage===currentPage}/>
    </div>
  );
}
